import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeftIcon,
  TrashIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  PlusIcon,
  StarIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { hubAPI } from '../../../services/hubApi';
import { wordpressAPI } from '../../../services/wordpressApi';
import {
  Hub,
  HubResource,
  HubSection,
  HubSectionType,
  CreateHubInput,
  UpdateHubInput,
  HubStatus,
} from '../../../types/hub';
import { WPPost } from '../../../types/wordpress';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Card from '../../../components/ui/Card';
import LoadingSkeleton from '../../../components/ui/LoadingSkeleton';
import ErrorState from '../../../components/ui/ErrorState';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import { useToast } from '../../../hooks/useToast';
import ToastContainer from '../../../components/ui/ToastContainer';
import { useUnsavedChanges } from '../../../hooks/useUnsavedChanges';
import ResourcePicker from '../../../components/admin/ResourcePicker';
import RichTextEditor, { RichTextEditorHandle } from '../../../components/admin/RichTextEditor';
import { hubProseClassName } from '../../../utils/hubProse';
import { decodeHtmlEntities } from '../../../utils/htmlEntities';
import { usePageTitle } from '../../../hooks/usePageTitle';
import { toolkitPath } from '../../../utils/placePaths';

function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export default function HubEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = !!id;

  const [title, setTitle] = useState('');
  usePageTitle(isEditing ? (title || 'Edit Hub') : 'New Hub');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<HubStatus>('DRAFT');
  const [featured, setFeatured] = useState(false);
  const [slugEdited, setSlugEdited] = useState(false);

  const [hub, setHub] = useState<Hub | null>(null);
  const [sections, setSections] = useState<HubSection[]>([]);
  const [resources, setResources] = useState<HubResource[]>([]);
  const [resourcePosts, setResourcePosts] = useState<Map<number, WPPost>>(new Map());
  const [loading, setLoading] = useState(isEditing);
  const [error, setError] = useState<Error | null>(null);
  const [saving, setSaving] = useState(false);
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
  const [sectionDraft, setSectionDraft] = useState({ title: '', body: '', description: '' });
  const [uploadingImage, setUploadingImage] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const richTextEditorRef = useRef<RichTextEditorHandle>(null);
  const { toasts, success, error: showError, removeToast } = useToast();

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSavedData, setLastSavedData] = useState('');

  useUnsavedChanges({ hasUnsavedChanges });

  useEffect(() => {
    if (!isEditing || !id) return;

    const fetchHub = async () => {
      try {
        setLoading(true);
        setError(null);
        const hubData = await hubAPI.fetchHub(id);
        setHub(hubData);
        setTitle(hubData.title);
        setSlug(hubData.slug);
        setDescription(hubData.description || '');
        setStatus(hubData.status || 'PUBLISHED');
        setFeatured(hubData.featured === true);
        setSlugEdited(true);
        setSections(hubData.sections || []);
        setResources(hubData.resources || []);
        setLastSavedData(
          JSON.stringify({
            title: hubData.title,
            slug: hubData.slug,
            description: hubData.description || '',
            status: hubData.status || 'PUBLISHED',
            featured: hubData.featured === true,
          })
        );

        const postsMap = new Map<number, WPPost>();
        await Promise.all(
          (hubData.resources || []).map(async (resource) => {
            try {
              const post = await wordpressAPI.fetchPost(resource.wpPostId);
              postsMap.set(resource.wpPostId, post);
            } catch (err) {
              console.warn(`Failed to fetch resource post ${resource.wpPostId}:`, err);
            }
          })
        );
        setResourcePosts(postsMap);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to load hub'));
      } finally {
        setLoading(false);
      }
    };

    fetchHub();
  }, [isEditing, id]);

  useEffect(() => {
    if (!isEditing) return;
    const current = JSON.stringify({ title, slug, description, status, featured });
    setHasUnsavedChanges(current !== lastSavedData && lastSavedData !== '');
  }, [title, slug, description, status, featured, lastSavedData, isEditing]);

  const refreshHub = async () => {
    if (!id) return;
    const hubData = await hubAPI.fetchHub(id);
    setHub(hubData);
    setSections(hubData.sections || []);
    setResources(hubData.resources || []);
  };

  const handleTitleChange = (value: string) => {
    setTitle(value);
    if (!slugEdited) {
      setSlug(slugify(value));
    }
  };

  const handleCreateSection = async (type: HubSectionType) => {
    if (!id) return;
    try {
      const section = await hubAPI.createSection(id, {
        type,
        title: type === 'CONTENT' ? 'New content section' : 'New resource group',
      });
      setSections([...sections, section].sort((a, b) => a.order - b.order));
      setEditingSectionId(section.id);
      setSectionDraft({
        title: section.title,
        body: section.body || '',
        description: section.description || '',
      });
      success('Section added');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to add section');
    }
  };

  const startEditSection = (section: HubSection) => {
    setEditingSectionId(section.id);
    setSectionDraft({
      title: section.title,
      body: section.body || '',
      description: section.description || '',
    });
  };

  const handleSaveSection = async () => {
    if (!id || !editingSectionId) return;
    const section = sections.find((s) => s.id === editingSectionId);
    if (!section) return;

    try {
      const updated = await hubAPI.updateSection(id, editingSectionId, {
        title: sectionDraft.title.trim() || section.title,
        body: section.type === 'CONTENT' ? sectionDraft.body : null,
        description: section.type === 'RESOURCE_GROUP' ? sectionDraft.description : null,
      });
      setSections(sections.map((s) => (s.id === updated.id ? { ...s, ...updated } : s)));
      setEditingSectionId(null);
      success('Section saved');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to save section');
    }
  };

  const handleUploadContentImage = async (fileList: FileList | null) => {
    const file = fileList?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showError('Please choose an image file');
      return;
    }

    try {
      setUploadingImage(true);
      const alt = file.name.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' ');
      const media = await wordpressAPI.uploadMedia(file, { alt });
      richTextEditorRef.current?.insertImage(media.source_url, media.alt_text || alt);
      success('Image uploaded and inserted into the section');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to upload image');
    } finally {
      setUploadingImage(false);
      if (imageInputRef.current) {
        imageInputRef.current.value = '';
      }
    }
  };

  const handleDeleteSection = async (sectionId: string) => {
    if (!id) return;
    if (!confirm('Delete this section? Resources in it will become ungrouped.')) return;
    try {
      await hubAPI.deleteSection(id, sectionId);
      setSections(sections.filter((s) => s.id !== sectionId));
      setResources(
        resources.map((r) => (r.sectionId === sectionId ? { ...r, sectionId: null } : r))
      );
      if (editingSectionId === sectionId) setEditingSectionId(null);
      success('Section deleted');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete section');
    }
  };

  const handleMoveSection = async (sectionId: string, direction: 'up' | 'down') => {
    if (!id) return;
    const ordered = [...sections].sort((a, b) => a.order - b.order);
    const index = ordered.findIndex((s) => s.id === sectionId);
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (index < 0 || newIndex < 0 || newIndex >= ordered.length) return;

    const swapped = [...ordered];
    [swapped[index], swapped[newIndex]] = [swapped[newIndex], swapped[index]];
    const sectionIds = swapped.map((s) => s.id);
    setSections(swapped.map((s, i) => ({ ...s, order: i })));

    try {
      const updated = await hubAPI.reorderSections(id, sectionIds);
      setSections(updated);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to reorder sections');
      await refreshHub();
    }
  };

  const handleAddResource = async (post: WPPost, sectionId?: string | null) => {
    if (!id) return;
    try {
      const resource = await hubAPI.addResourceToHub(id, {
        wpPostId: post.id,
        sectionId: sectionId || null,
        order: resources.filter((r) => r.sectionId === sectionId).length,
      });
      setResources([...resources, resource]);
      setResourcePosts(new Map(resourcePosts).set(post.id, post));
      success('Resource added');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to add resource');
    }
  };

  const handleRemoveResource = async (resourceId: string) => {
    if (!id) return;
    try {
      const resource = resources.find((r) => r.id === resourceId);
      await hubAPI.removeResourceFromHub(id, resourceId);
      setResources(resources.filter((r) => r.id !== resourceId));
      if (resource) {
        const next = new Map(resourcePosts);
        next.delete(resource.wpPostId);
        setResourcePosts(next);
      }
      success('Resource removed');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to remove resource');
    }
  };

  const handleToggleHighlight = async (resource: HubResource) => {
    if (!id) return;
    try {
      const updated = await hubAPI.updateHubResource(id, resource.id, {
        isHighlighted: !resource.isHighlighted,
      });
      setResources(resources.map((r) => (r.id === updated.id ? updated : r)));
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to update highlight');
    }
  };

  const handleAssignResourceSection = async (resourceId: string, sectionId: string | null) => {
    if (!id) return;
    try {
      const updated = await hubAPI.updateHubResource(id, resourceId, { sectionId });
      setResources(resources.map((r) => (r.id === updated.id ? updated : r)));
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to move resource');
    }
  };

  const handleMoveResource = async (resourceId: string, direction: 'up' | 'down') => {
    if (!id) return;
    const resource = resources.find((r) => r.id === resourceId);
    if (!resource) return;

    const siblings = resources
      .filter((r) => r.sectionId === resource.sectionId)
      .sort((a, b) => a.order - b.order);
    const index = siblings.findIndex((r) => r.id === resourceId);
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (index < 0 || newIndex < 0 || newIndex >= siblings.length) return;

    const a = siblings[index];
    const b = siblings[newIndex];
    try {
      const [updatedA, updatedB] = await Promise.all([
        hubAPI.updateHubResource(id, a.id, { order: b.order }),
        hubAPI.updateHubResource(id, b.id, { order: a.order }),
      ]);
      setResources(
        resources.map((r) => {
          if (r.id === updatedA.id) return updatedA;
          if (r.id === updatedB.id) return updatedB;
          return r;
        })
      );
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to reorder resource');
    }
  };

  const handleSave = async () => {
    if (!title.trim() || !slug.trim()) {
      showError('Title and slug are required');
      return;
    }

    try {
      setSaving(true);
      if (isEditing && id) {
        const updates: UpdateHubInput = {
          title: title.trim(),
          slug: slug.trim(),
          description: description.trim() || undefined,
          status,
          featured,
        };
        const updated = await hubAPI.updateHub(id, updates);
        setHub(updated);
        setStatus(updated.status);
        setFeatured(updated.featured === true);
        setLastSavedData(JSON.stringify({ title, slug, description, status, featured }));
        setHasUnsavedChanges(false);
        success('Hub saved');
      } else {
        const input: CreateHubInput = {
          title: title.trim(),
          slug: slug.trim(),
          description: description.trim() || undefined,
          status,
          featured,
        };
        const created = await hubAPI.createHub(input);
        success('Hub created');
        navigate(`/admin/hubs/${created.id}/edit`);
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to save hub');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-8">
        <LoadingSkeleton lines={10} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-8">
        <ErrorState error={error} title="Failed to Load Hub" onRetry={() => window.location.reload()} />
      </div>
    );
  }

  const orderedSections = [...sections].sort((a, b) => a.order - b.order);
  const resourceGroups = orderedSections.filter((s) => s.type === 'RESOURCE_GROUP');
  const ungroupedResources = resources.filter((r) => !r.sectionId);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <ToastContainer toasts={toasts} onClose={removeToast} />
      <Breadcrumb
        items={[
          { label: 'Admin', to: '/admin' },
          { label: 'Hubs', to: '/admin/hubs' },
          { label: isEditing ? 'Edit Hub' : 'Create Hub' },
        ]}
      />

      <Link
        to="/admin/hubs"
        className="inline-flex items-center gap-2 text-primary hover:text-primary-dark mb-6 transition-colors"
      >
        <ArrowLeftIcon className="h-5 w-5" />
        <span>Back to Hubs</span>
      </Link>

      <h1 className="text-3xl font-bold text-gray-900 mb-6">
        {isEditing ? 'Edit Hub' : 'Create Hub'}
      </h1>

      <Card className="mb-8">
        <div className="p-6 space-y-4">
          <Input
            label="Title"
            value={title}
            onChange={(e) => handleTitleChange(e.target.value)}
            required
          />
          <Input
            label="Slug"
            value={slug}
            onChange={(e) => {
              setSlugEdited(true);
              setSlug(slugify(e.target.value));
            }}
            required
          />
          <p className="text-sm text-gray-500 -mt-2">Used in the URL: /toolkits/your-slug, or /your-slug when the toolkit belongs to a program.</p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              value={status}
              onChange={(e) => setStatus(e.target.value as HubStatus)}
            >
              <option value="DRAFT">Draft — hidden from public site</option>
              <option value="PUBLISHED">Published — visible on public site</option>
            </select>
          </div>
          <label className="flex items-start gap-3 min-h-[44px] text-sm text-gray-700">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
              checked={featured}
              onChange={(e) => setFeatured(e.target.checked)}
            />
            <span>
              Feature on the home
              <span className="block text-gray-500">
                The home shows up to three published hubs with this checked. Drafts stay hidden.
              </span>
            </span>
          </label>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={() => navigate('/admin/hubs')}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving || !title.trim() || !slug.trim()}>
              {saving ? 'Saving...' : isEditing ? 'Save Details' : 'Create Hub'}
            </Button>
          </div>
        </div>
      </Card>

      {isEditing && id && (
        <>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Page sections</h2>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={() => handleCreateSection('CONTENT')}>
                <PlusIcon className="h-4 w-4 mr-1" />
                Content
              </Button>
              <Button variant="secondary" onClick={() => handleCreateSection('RESOURCE_GROUP')}>
                <PlusIcon className="h-4 w-4 mr-1" />
                Resource group
              </Button>
            </div>
          </div>

          {orderedSections.length === 0 && (
            <p className="text-gray-600 mb-8">
              Add content sections and resource groups to build the hub page.
            </p>
          )}

          <div className="space-y-4 mb-10">
            {orderedSections.map((section, index) => {
              const sectionResources = resources
                .filter((r) => r.sectionId === section.id)
                .sort((a, b) => a.order - b.order);

              return (
                <Card key={section.id}>
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div>
                        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                          {section.type === 'CONTENT' ? 'Content' : 'Resource group'}
                        </p>
                        <h3 className="text-lg font-semibold text-gray-900">{section.title}</h3>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          className="!px-2 !py-2"
                          onClick={() => handleMoveSection(section.id, 'up')}
                          disabled={index === 0}
                          aria-label="Move section up"
                        >
                          <ArrowUpIcon className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          className="!px-2 !py-2"
                          onClick={() => handleMoveSection(section.id, 'down')}
                          disabled={index === orderedSections.length - 1}
                          aria-label="Move section down"
                        >
                          <ArrowDownIcon className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" className="!px-3 !py-2" onClick={() => startEditSection(section)}>
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          className="!px-2 !py-2"
                          onClick={() => handleDeleteSection(section.id)}
                          aria-label="Delete section"
                        >
                          <TrashIcon className="h-4 w-4 text-error" />
                        </Button>
                      </div>
                    </div>

                    {editingSectionId === section.id ? (
                      <div className="space-y-3 border-t border-gray-100 pt-4">
                        <Input
                          label="Section title"
                          value={sectionDraft.title}
                          onChange={(e) =>
                            setSectionDraft({ ...sectionDraft, title: e.target.value })
                          }
                        />
                        {section.type === 'CONTENT' ? (
                          <div>
                            <input
                              ref={imageInputRef}
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => handleUploadContentImage(e.target.files)}
                            />
                            <RichTextEditor
                              key={editingSectionId}
                              ref={richTextEditorRef}
                              label="Body"
                              value={sectionDraft.body}
                              onChange={(body) =>
                                setSectionDraft({ ...sectionDraft, body })
                              }
                              onUploadImageClick={() => imageInputRef.current?.click()}
                              uploadingImage={uploadingImage}
                            />
                          </div>
                        ) : (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Description
                            </label>
                            <textarea
                              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                              rows={3}
                              value={sectionDraft.description}
                              onChange={(e) =>
                                setSectionDraft({ ...sectionDraft, description: e.target.value })
                              }
                            />
                          </div>
                        )}
                        <div className="flex gap-2">
                          <Button onClick={handleSaveSection}>Save section</Button>
                          <Button variant="secondary" onClick={() => setEditingSectionId(null)}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        {section.type === 'CONTENT' && section.body && (
                          <div
                            className={`${hubProseClassName} prose-sm border-t border-gray-100 pt-3`}
                            dangerouslySetInnerHTML={{
                              __html: section.body,
                            }}
                          />
                        )}
                        {section.type === 'RESOURCE_GROUP' && section.description && (
                          <p className="text-sm text-gray-600 border-t border-gray-100 pt-3">
                            {section.description}
                          </p>
                        )}
                      </>
                    )}

                    {section.type === 'RESOURCE_GROUP' && (
                      <div className="mt-4 border-t border-gray-100 pt-4">
                        <ResourcePicker
                          excludeIds={resources.map((r) => r.wpPostId)}
                          onSelect={(post) => handleAddResource(post, section.id)}
                        />
                        <div className="mt-3 space-y-2">
                          {sectionResources.map((resource, rIndex) => {
                            const post = resourcePosts.get(resource.wpPostId);
                            return (
                              <div
                                key={resource.id}
                                className="flex items-center justify-between gap-3 rounded-md border border-gray-200 px-3 py-2"
                              >
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-medium text-gray-900 truncate">
                                    {decodeHtmlEntities(post?.title.rendered) || `Resource ${resource.wpPostId}`}
                                  </p>
                                </div>
                                <div className="flex items-center gap-1">
                                  <button
                                    type="button"
                                    className="p-1 text-amber-500 hover:text-amber-600"
                                    title={
                                      resource.isHighlighted
                                        ? 'Remove highlight'
                                        : 'Highlight resource'
                                    }
                                    onClick={() => handleToggleHighlight(resource)}
                                  >
                                    {resource.isHighlighted ? (
                                      <StarIconSolid className="h-5 w-5" />
                                    ) : (
                                      <StarIcon className="h-5 w-5" />
                                    )}
                                  </button>
                                  <Button
                                    variant="ghost"
                                    className="!px-2 !py-2"
                                    onClick={() => handleMoveResource(resource.id, 'up')}
                                    disabled={rIndex === 0}
                                  >
                                    <ArrowUpIcon className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    className="!px-2 !py-2"
                                    onClick={() => handleMoveResource(resource.id, 'down')}
                                    disabled={rIndex === sectionResources.length - 1}
                                  >
                                    <ArrowDownIcon className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    className="!px-2 !py-2"
                                    onClick={() => handleRemoveResource(resource.id)}
                                  >
                                    <TrashIcon className="h-4 w-4 text-error" />
                                  </Button>
                                </div>
                              </div>
                            );
                          })}
                          {sectionResources.length === 0 && (
                            <p className="text-sm text-gray-500">No resources in this group yet.</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>

          <Card className="mb-8">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Ungrouped resources</h2>
              <p className="text-sm text-gray-600 mb-4">
                Resources not assigned to a resource group. Assign them below or add them into a
                group above.
              </p>
              <ResourcePicker
                excludeIds={resources.map((r) => r.wpPostId)}
                onSelect={(post) => handleAddResource(post, null)}
              />
              <div className="mt-4 space-y-2">
                {ungroupedResources.map((resource) => {
                  const post = resourcePosts.get(resource.wpPostId);
                  return (
                    <div
                      key={resource.id}
                      className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-gray-200 px-3 py-2"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {decodeHtmlEntities(post?.title.rendered) || `Resource ${resource.wpPostId}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          className="p-1 text-amber-500 hover:text-amber-600"
                          onClick={() => handleToggleHighlight(resource)}
                          title={
                            resource.isHighlighted ? 'Remove highlight' : 'Highlight resource'
                          }
                        >
                          {resource.isHighlighted ? (
                            <StarIconSolid className="h-5 w-5" />
                          ) : (
                            <StarIcon className="h-5 w-5" />
                          )}
                        </button>
                        <select
                          className="rounded-md border border-gray-300 text-sm px-2 py-1"
                          value={resource.sectionId || ''}
                          onChange={(e) =>
                            handleAssignResourceSection(resource.id, e.target.value || null)
                          }
                        >
                          <option value="">Ungrouped</option>
                          {resourceGroups.map((group) => (
                            <option key={group.id} value={group.id}>
                              {group.title}
                            </option>
                          ))}
                        </select>
                        <Button
                          variant="ghost"
                          className="!px-2 !py-2"
                          onClick={() => handleRemoveResource(resource.id)}
                        >
                          <TrashIcon className="h-4 w-4 text-error" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
                {ungroupedResources.length === 0 && (
                  <p className="text-sm text-gray-500">No ungrouped resources.</p>
                )}
              </div>
            </div>
          </Card>

          {hub && hub.status === 'PUBLISHED' && (
            <p className="text-sm text-gray-500">
              Public page:{' '}
              <Link className="text-primary hover:underline" to={toolkitPath(hub)}>
                {toolkitPath(hub)}
              </Link>
            </p>
          )}
          {hub && hub.status === 'DRAFT' && (
            <p className="text-sm text-amber-700">
              This hub is a draft and is not visible on the public site until published.
            </p>
          )}
        </>
      )}
    </div>
  );
}
